<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { sendNewsletter } from '@/api/newsletters'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import type { InsufficientCreditsError, SendNewsletterSuccess } from '@/types/newsletter'

const LARGE_SEND_THRESHOLD = 1000

const props = defineProps<{
  newsletterId: number
  organisationId: number
  recipientCount: number
  creditBalance: number
  orgSlug: string
}>()

const emit = defineEmits<{
  confirmed: [result: SendNewsletterSuccess]
  cancel: []
}>()

const { t } = useI18n()
const router = useRouter()

// Form state
const sendMode = ref<'now' | 'schedule'>('now')
const scheduledAt = ref<string>('')
const confirmChecked = ref<boolean>(false)
const submitting = ref<boolean>(false)
const error = ref<string | null>(null)

// Insufficient credits state
const insufficientCredits = ref<InsufficientCreditsError | null>(null)

// Computed
const creditCost = computed<number>(() => props.recipientCount)
const balanceAfter = computed<number>(() => props.creditBalance - creditCost.value)
const hasEnoughCredits = computed<boolean>(() => props.creditBalance >= creditCost.value)
const shortfall = computed<number>(() => Math.max(0, creditCost.value - props.creditBalance))
const isLargeSend = computed<boolean>(() => props.recipientCount > LARGE_SEND_THRESHOLD)

const canSubmit = computed<boolean>(() => {
  if (!confirmChecked.value) return false
  if (!hasEnoughCredits.value) return false
  if (sendMode.value === 'schedule' && !scheduledAt.value) return false
  return true
})

function formatCount(n: number): string {
  return n.toLocaleString()
}

function goToBuyCredits(): void {
  void router.push({
    path: `/organizer/organisations/${props.orgSlug}/credits`,
    query: { prefill: String(shortfall.value) },
  })
  emit('cancel')
}

function isAxiosError(e: unknown): e is { response: { status: number; data: unknown } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object'
  )
}

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) return
  submitting.value = true
  error.value = null
  insufficientCredits.value = null

  try {
    const payload = sendMode.value === 'schedule' && scheduledAt.value
      ? { scheduled_at: new Date(scheduledAt.value).toISOString() }
      : {}

    const result = await sendNewsletter(props.organisationId, props.newsletterId, payload)
    emit('confirmed', result)
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      const response = e.response as { status: number; data: unknown }
      if (response.status === 402) {
        // Insufficient credits — swap CTA to "Buy credits"
        insufficientCredits.value = response.data as InsufficientCreditsError
      } else if (response.status === 409) {
        error.value = t('newsletters.send_modal.error_not_editable')
      } else if (response.status === 422) {
        const data = response.data as { message?: string }
        error.value = data.message ?? t('newsletters.send_modal.error_no_recipients')
      } else {
        error.value = t('newsletters.send_modal.error_generic')
      }
    } else {
      error.value = t('newsletters.send_modal.error_generic')
    }
  } finally {
    submitting.value = false
  }
}

function actionLabel(): string {
  if (sendMode.value === 'schedule' && scheduledAt.value) {
    const d = new Date(scheduledAt.value)
    return t('newsletters.send_modal.send_action_scheduled', {
      datetime: d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
    })
  }
  return t('newsletters.send_modal.send_action_now', { count: formatCount(props.recipientCount) })
}

// Minimum datetime for schedule picker (must be future)
function minDateTime(): string {
  const now = new Date()
  now.setMinutes(now.getMinutes() + 5)
  // datetime-local format: YYYY-MM-DDTHH:mm
  return now.toISOString().slice(0, 16)
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    role="dialog"
    aria-modal="true"
    :aria-label="t('newsletters.send_modal.aria_label')"
    @click.self="emit('cancel')"
  >
    <div class="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl flex flex-col max-h-[90vh]">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-slate-700 shrink-0">
        <h2 class="text-lg font-bold text-white">{{ t('newsletters.send_modal.header_title') }}</h2>
        <button
          type="button"
          class="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          :aria-label="t('newsletters.send_modal.close_aria')"
          @click="emit('cancel')"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Body (scrollable) -->
      <div class="flex-1 overflow-y-auto px-6 py-5 space-y-5">

        <!-- Insufficient credits banner -->
        <div
          v-if="insufficientCredits"
          class="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3"
        >
          <p class="text-sm font-semibold text-amber-400 mb-1">{{ t('newsletters.send_modal.insufficient_credits_title') }}</p>
          <p class="text-sm text-slate-400">
            {{ t('newsletters.send_modal.insufficient_credits_body', {
              required: formatCount(insufficientCredits.required),
              available: formatCount(insufficientCredits.available),
              shortfall: formatCount(shortfall),
            }) }}
          </p>
        </div>

        <!-- Large send guard -->
        <div
          v-if="isLargeSend && !insufficientCredits"
          class="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3"
        >
          <p class="text-sm font-semibold text-amber-400">{{ t('newsletters.send_modal.large_send_title') }}</p>
          <p class="text-sm text-slate-400 mt-0.5">
            {{ t('newsletters.send_modal.large_send_body') }}
          </p>
        </div>

        <!-- Cost ledger -->
        <div class="rounded-xl bg-slate-900/50 border border-slate-700 px-4 py-4 space-y-2.5 text-sm">
          <div class="flex items-center justify-between">
            <span class="text-slate-400">{{ t('newsletters.send_modal.ledger_recipients') }}</span>
            <span class="text-white font-medium">{{ formatCount(recipientCount) }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-slate-400">{{ t('newsletters.send_modal.ledger_cost') }}</span>
            <span class="text-white font-medium">{{ t('newsletters.send_modal.ledger_cost_value', { count: formatCount(creditCost) }) }}</span>
          </div>
          <hr class="border-slate-700" />
          <div class="flex items-center justify-between">
            <span class="text-slate-400">{{ t('newsletters.send_modal.ledger_balance_now') }}</span>
            <span class="text-white font-medium">{{ t('newsletters.send_modal.ledger_balance_now_value', { count: formatCount(creditBalance) }) }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-slate-400">{{ t('newsletters.send_modal.ledger_balance_after') }}</span>
            <span
              class="font-semibold"
              :class="balanceAfter >= 0 ? 'text-emerald-400' : 'text-red-400'"
            >
              {{ t('newsletters.send_modal.ledger_balance_after_value', { count: formatCount(Math.max(0, balanceAfter)) }) }}
            </span>
          </div>
        </div>

        <!-- Send timing -->
        <div v-if="!insufficientCredits" class="space-y-3">
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">{{ t('newsletters.send_modal.when_to_send_heading') }}</p>
          <div class="space-y-2">
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="sendMode"
                type="radio"
                value="now"
                class="text-emerald-500 border-slate-600 bg-slate-800 focus:ring-emerald-500"
              />
              <span class="text-sm text-white">{{ t('newsletters.send_modal.send_now_label') }}</span>
            </label>
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="sendMode"
                type="radio"
                value="schedule"
                class="text-emerald-500 border-slate-600 bg-slate-800 focus:ring-emerald-500"
              />
              <span class="text-sm text-white">{{ t('newsletters.send_modal.schedule_label') }}</span>
            </label>
          </div>

          <div v-if="sendMode === 'schedule'" class="mt-2">
            <input
              v-model="scheduledAt"
              type="datetime-local"
              :min="minDateTime()"
              class="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
            />
          </div>
        </div>

        <!-- Confirm checkbox -->
        <div v-if="!insufficientCredits">
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              v-model="confirmChecked"
              type="checkbox"
              class="mt-0.5 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
            />
            <span class="text-sm text-slate-300">
              {{ t('newsletters.send_modal.confirm_checkbox', { count: formatCount(recipientCount) }) }}
            </span>
          </label>
        </div>

        <!-- Error message -->
        <div
          v-if="error"
          class="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
        >
          {{ error }}
        </div>
      </div>

      <!-- Footer -->
      <div class="shrink-0 px-6 py-4 border-t border-slate-700 flex items-center justify-between gap-3">
        <button
          type="button"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          @click="emit('cancel')"
        >
          {{ t('newsletters.send_modal.cancel_button') }}
        </button>

        <!-- Buy credits CTA — shown when balance is insufficient -->
        <button
          v-if="insufficientCredits || !hasEnoughCredits"
          type="button"
          class="bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-lg px-5 py-2 text-sm transition-colors flex items-center gap-2"
          @click="goToBuyCredits"
        >
          {{ t('newsletters.send_modal.buy_credits_button', { count: formatCount(shortfall) }) }}
        </button>

        <!-- Send / schedule button -->
        <button
          v-else
          type="button"
          :disabled="!canSubmit || submitting"
          class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2 text-sm transition-colors flex items-center gap-2"
          @click="handleSubmit"
        >
          <AppSpinner v-if="submitting" size="button" />
          {{ submitting ? t('newsletters.send_modal.sending') : actionLabel() }}
        </button>
      </div>
    </div>
  </div>
</template>
